import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2, Minus, Plus, ShoppingBag, ArrowRight } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const CartPage = () => {
  const navigate = useNavigate();
  const { items, updateQuantity, removeItem, subtotal, itemCount } = useCart();
  const { isAuthenticated } = useAuth();

  const formatPrice = (price) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(price);
  };

  const handleProceedToCheckout = () => {
    if (!isAuthenticated) {
      toast.error('Accedi per procedere al checkout');
      navigate('/login', { state: { from: { pathname: '/carrello' } } });
      return;
    }
    navigate('/checkout');
  };

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-background" data-testid="cart-page-empty">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <ShoppingBag className="w-16 h-16 mx-auto text-text-muted mb-4" />
            <h1 className="font-heading text-2xl font-bold text-text-primary mb-2">
              Il tuo carrello è vuoto
            </h1>
            <p className="text-text-secondary mb-8">
              Scopri i nostri prodotti e inizia a fare acquisti
            </p>
            <Button asChild>
              <Link to="/prodotti">
                Esplora i Prodotti
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="cart-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="font-heading text-3xl font-bold text-text-primary mb-8">
          Carrello ({itemCount} {itemCount === 1 ? 'articolo' : 'articoli'})
        </h1>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {items.map(({ product, quantity }) => (
              <div 
                key={product.id}
                className="bg-white p-4 rounded-xl border border-border flex gap-4"
                data-testid={`cart-item-${product.id}`}
              >
                <Link to={`/prodotti/${product.slug}`} className="flex-shrink-0">
                  <img
                    src={product.image_url || 'https://images.pexels.com/photos/7663192/pexels-photo-7663192.jpeg'}
                    alt={product.name}
                    className="w-24 h-24 object-cover rounded-lg"
                  />
                </Link>
                
                <div className="flex-1 min-w-0">
                  <Link to={`/prodotti/${product.slug}`} className="hover:text-primary transition-colors">
                    <h3 className="font-semibold text-text-primary truncate">{product.name}</h3>
                  </Link>
                  <p className="text-primary font-bold mt-1">{formatPrice(product.price)}</p>
                  
                  <div className="flex items-center justify-between mt-3">
                    <div className="flex items-center border border-border rounded-lg">
                      <button
                        onClick={() => updateQuantity(product.id, quantity - 1)}
                        className="p-2 hover:bg-background transition-colors"
                        data-testid={`decrease-${product.id}`}
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                      <span className="w-8 text-center text-sm font-medium">{quantity}</span>
                      <button
                        onClick={() => updateQuantity(product.id, quantity + 1)}
                        className="p-2 hover:bg-background transition-colors"
                        disabled={quantity >= product.inventory}
                        data-testid={`increase-${product.id}`}
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                    
                    <button
                      onClick={() => removeItem(product.id)}
                      className="p-2 text-text-muted hover:text-red-500 transition-colors"
                      data-testid={`remove-${product.id}`}
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                <div className="hidden sm:block text-right">
                  <p className="font-bold text-lg">{formatPrice(product.price * quantity)}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white p-6 rounded-xl border border-border sticky top-24">
              <h2 className="font-heading text-xl font-bold mb-4">Riepilogo Ordine</h2>
              
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-text-secondary">Subtotale</span>
                  <span className="font-medium">{formatPrice(subtotal)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-text-secondary">Spedizione</span>
                  <span className="text-secondary font-medium">Calcolata al checkout</span>
                </div>
              </div>

              <div className="border-t border-border mt-4 pt-4">
                <div className="flex justify-between text-lg font-bold">
                  <span>Totale</span>
                  <span className="text-primary">{formatPrice(subtotal)}</span>
                </div>
              </div>

              <Button 
                className="w-full mt-6" 
                size="lg"
                onClick={handleProceedToCheckout}
                data-testid="proceed-checkout-btn"
              >
                Procedi al Checkout
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>

              <Link 
                to="/prodotti" 
                className="block text-center text-sm text-primary hover:underline mt-4"
              >
                Continua lo shopping
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartPage;
